import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { isValidEmail, normalizeEmail } from "@/lib/validate";
import SeoHead from "@/components/SeoHead";
import { SITE_URL } from "@/seo/data";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import FileUpload from "@/components/FileUpload";
import AutocompleteInput from "@/components/AutocompleteInput";
import { searchBrands, searchModels } from "@/data/vehicles";
import { isDateDisabled, getBookingHint, toLocalDateString, parseLocalDateString } from "@/lib/booking";
import { ArrowRight, CarProfile, FileText, CurrencyEur, CalendarBlank, CreditCard, SpinnerGap, MagicWand, Info, CheckCircle, Sparkle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const FEATURES = [
  { v: "rain_sensor", label: "Regensensor" },
  { v: "camera", label: "Kamera / ADAS" },
  { v: "heated", label: "Beheizbar" },
  { v: "acoustic", label: "Akustikscheibe" },
];

const TIME_SLOTS = ["09:00", "11:00", "13:00", "15:00", "17:00"];

export default function SelfPayer() {
  const navigate = useNavigate();
  const { claimId: paramId } = useParams();
  const [claimId, setClaimId] = useState(paramId || null);
  const [claim, setClaim] = useState(null);
  const [stage, setStage] = useState(1); // 1: upload, 2: confirm, 3: estimate, 4: appointment, 5: pay
  const [loading, setLoading] = useState(false);
  const [fahrzeugschein, setFahrzeugschein] = useState(null);
  const [vehicle, setVehicle] = useState({ manufacturer: "", model: "", year: "", license_plate: "", vin: "" });
  const [features, setFeatures] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [appointment, setAppointment] = useState({ date: "", time: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  // VIN-Scheiben-Assistent
  const [vinLoading, setVinLoading] = useState(false);
  const [vinResult, setVinResult] = useState(null);       // { manufacturer, model, year, body_type, glass_options: [...] }
  const [glassPosition, setGlassPosition] = useState(null); // position_key
  const [glassFeatures, setGlassFeatures] = useState({});   // { feature_key: bool }
  const [glassEstimate, setGlassEstimate] = useState(null); // { low, high }

  useEffect(() => {
    if (paramId) {
      api.get(`/claims/${paramId}`).then(({ data }) => setClaim(data));
    }
  }, [paramId]);

  const ensureClaim = async () => {
    if (claimId) return claimId;
    const { data } = await api.post("/claims", { flow_type: "self_pay" });
    setClaimId(data.id);
    setClaim(data);
    return data.id;
  };

  const handleUploadFahrzeugschein = async (f) => {
    setFahrzeugschein(f);
    if (!f) return;
    const id = await ensureClaim();
    await api.post(`/claims/${id}/files`, { ...f, category: "fahrzeugschein" });
  };

  const runOCR = async () => {
    if (!fahrzeugschein) {
      toast.error("Bitte zuerst Fahrzeugschein hochladen");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${api.defaults.baseURL}/files/${fahrzeugschein.storage_path}`);
      const blob = await res.blob();
      const form = new FormData();
      form.append("file", blob, fahrzeugschein.original_filename);
      form.append("doc_type", "vehicle_registration");
      const { data } = await api.post("/ai/ocr", form, { headers: { "Content-Type": "multipart/form-data" } });
      setVehicle({
        manufacturer: data.manufacturer || "",
        model: data.model || "",
        year: data.year ? String(data.year) : "",
        license_plate: data.license_plate || "",
        vin: data.vin || "",
      });
      toast.success("Fahrzeugdaten erkannt!");
      setStage(2);
    } catch (e) {
      toast.error("OCR fehlgeschlagen. Bitte Daten manuell eingeben.");
      setStage(2);
    } finally {
      setLoading(false);
    }
  };

  const computeEstimate = async () => {
    if (!vehicle.manufacturer || !vehicle.model) {
      toast.error("Hersteller und Modell sind erforderlich");
      return;
    }
    setLoading(true);
    try {
      const id = await ensureClaim();
      await api.patch(`/claims/${id}`, {
        manufacturer: vehicle.manufacturer,
        model: vehicle.model,
        year: vehicle.year,
        license_plate: vehicle.license_plate,
        vin: vehicle.vin,
      });
      const { data } = await api.post("/ai/estimate-price", {
        claim_id: id,
        manufacturer: vehicle.manufacturer,
        model: vehicle.model,
        year: vehicle.year,
        features,
      });
      setEstimate(data);
      setStage(3);
    } catch (e) {
      toast.error("Preisschätzung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const decodeVin = async () => {
    const vin = (vehicle.vin || "").trim().toUpperCase();
    if (vin.length !== 17) {
      toast.error("VIN muss genau 17 Zeichen haben");
      return;
    }
    setVinLoading(true);
    try {
      const { data } = await api.post("/vin/decode", { vin });
      if (!data.ok) {
        toast.error(data.error || "VIN konnte nicht dekodiert werden");
        setVinResult(null);
        return;
      }
      setVinResult(data);
      setVehicle((v) => ({
        ...v,
        manufacturer: data.manufacturer || v.manufacturer,
        model: data.model || v.model,
        year: data.year || v.year,
      }));
      // Default: Frontscheibe wählen + Auto-erkannte Features direkt anwenden
      const defaultOpt = data.glass_options?.[0];
      if (defaultOpt) {
        setGlassPosition(defaultOpt.position_key);
        setGlassFeatures(defaultOpt.inferred_features || {});
        setGlassEstimate({ low: defaultOpt.price_low, high: defaultOpt.price_high });
      }
      toast.success(`${data.manufacturer || "Fahrzeug"} ${data.model || ""} erkannt`);
    } catch (e) {
      toast.error("VIN-Prüfung fehlgeschlagen");
    } finally {
      setVinLoading(false);
    }
  };

  const recalcGlassEstimate = async (position, feats) => {
    if (!position) return;
    try {
      const { data } = await api.post("/glass/estimate", {
        position_key: position,
        features: feats,
      });
      setGlassEstimate(data);
    } catch (e) {
      /* silent */
    }
  };

  const toggleGlassFeature = (key) => {
    const next = { ...glassFeatures, [key]: !glassFeatures[key] };
    setGlassFeatures(next);
    recalcGlassEstimate(glassPosition, next);
  };

  const selectGlassPosition = (pos) => {
    setGlassPosition(pos);
    // Auto-erkannte Features für die neue Position übernehmen
    const opt = vinResult?.glass_options?.find((o) => o.position_key === pos);
    const inferred = opt?.inferred_features || {};
    setGlassFeatures(inferred);
    if (opt) {
      setGlassEstimate({ low: opt.price_low, high: opt.price_high });
    } else {
      setGlassEstimate(null);
      recalcGlassEstimate(pos, {});
    }
  };

  const useGlassPriceAsFinal = async () => {
    if (!glassEstimate || !glassPosition) return;
    setLoading(true);
    try {
      const id = await ensureClaim();
      const positionLabel = vinResult?.glass_options?.find((o) => o.position_key === glassPosition)?.label || glassPosition;
      // Aktive Features als lesbare Liste
      const meta = vinResult?.glass_options?.find((o) => o.position_key === glassPosition);
      const activeFeatures = (meta?.features || [])
        .filter((f) => glassFeatures[f.key])
        .map((f) => f.label);
      const explanation = activeFeatures.length
        ? `${positionLabel} · ${activeFeatures.join(" · ")}`
        : `${positionLabel} · Standard-Ausstattung`;

      const { data } = await api.patch(`/claims/${id}`, {
        manufacturer: vehicle.manufacturer,
        model: vehicle.model,
        year: vehicle.year,
        license_plate: vehicle.license_plate,
        vin: vehicle.vin,
        glass_position: positionLabel,
        glass_position_key: glassPosition,
        glass_features: glassFeatures,
      });
      setEstimate({
        low: data.estimated_price_low ?? glassEstimate.low,
        high: data.estimated_price_high ?? glassEstimate.high,
        explanation,
      });
      setStage(3);
    } catch (e) {
      toast.error("Preis konnte nicht übernommen werden");
    } finally {
      setLoading(false);
    }
  };

  const startDeposit = async () => {    setLoading(true);
    try {
      const id = await ensureClaim();
      await api.patch(`/claims/${id}`, {
        appointment_date: appointment.date,
        appointment_time: appointment.time,
        customer_name: contact.name,
        customer_email: normalizeEmail(contact.email),
        customer_phone: contact.phone,
      });
      const { data } = await api.post("/payments/checkout/session", {
        claim_id: id,
        payment_type: "deposit",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Zahlung konnte nicht gestartet werden");
    } finally {
      setLoading(false);
    }
  };

  const depositAmount = estimate ? Math.round(((estimate.low + estimate.high) / 2) * 0.25 * 100) / 100 : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-40 md:pb-20">
      <SeoHead
        title="Selbstzahler: Autoglas-Festpreis anfragen | Servus Autoglas"
        description="Ohne Versicherung? Steinschlag oder Scheibentausch zum fairen Preis: online anfragen, Sofort-Einschätzung erhalten und Termin in Ludwigshafen sichern."
        canonical={`${SITE_URL}/selbstzahler`}
      />
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold">
            <Sparkle size={14} weight="fill" /> KI-Preisschätzung
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 tracking-tight">Selbstzahler-Preis in Sekunden</h1>
          <p className="text-slate-600 mt-2">Laden Sie Ihren Fahrzeugschein hoch — wir erledigen den Rest.</p>
        </div>

        {claim?.tracking_code && (
          <div className="p-4 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Tracking-Code</p>
              <p className="text-lg font-bold text-slate-900" data-testid="self-pay-tracking-code">{claim.tracking_code}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(claim.tracking_code); toast.success("Kopiert"); }}>
              <Copy size={14} className="mr-1.5" /> Kopieren
            </Button>
          </div>
        )}

        {/* STAGE 1: Upload */}
        {stage === 1 && (
          <Card icon={FileText} title="Fahrzeugschein hochladen">
            <FileUpload
              label="Fahrzeugschein (Vorderseite)"
              category="fahrzeugschein"
              hint="Daten werden automatisch ausgelesen (HSN/TSN, Hersteller, Modell)"
              required
              onUploaded={handleUploadFahrzeugschein}
              initialFile={fahrzeugschein}
            />
            <Button
              onClick={runOCR}
              disabled={!fahrzeugschein || loading}
              className="h-14 w-full bg-blue-600 hover:bg-blue-700 text-base"
              data-testid="run-ocr-button"
            >
              {loading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <MagicWand size={18} className="mr-2" />}
              Mit KI auslesen
            </Button>
            <button onClick={() => setStage(2)} className="text-sm text-slate-500 hover:text-slate-900 underline mx-auto block" data-testid="skip-ocr">
              Daten manuell eingeben
            </button>
          </Card>
        )}

        {/* STAGE 2: Confirm vehicle */}
        {stage === 2 && (
          <Card icon={CarProfile} title="Fahrzeugdaten bestätigen">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Hersteller">
                <AutocompleteInput
                  value={vehicle.manufacturer}
                  onChange={(val) => setVehicle({ ...vehicle, manufacturer: val })}
                  onSelect={(val) => setVehicle({ ...vehicle, manufacturer: val, model: "" })}
                  getSuggestions={(q) => searchBrands(q, 10)}
                  placeholder="z.B. BMW, Ford, Audi …"
                  inputClassName="h-14 text-base"
                  testId="self-input-manufacturer"
                  emptyLabel="Keine passende Marke gefunden"
                />
              </Field>
              <Field label="Modell">
                <AutocompleteInput
                  value={vehicle.model}
                  onChange={(val) => setVehicle({ ...vehicle, model: val })}
                  onSelect={(val) => setVehicle({ ...vehicle, model: val })}
                  getSuggestions={(q) => searchModels(vehicle.manufacturer, q, 12)}
                  placeholder={vehicle.manufacturer ? "Modell wählen oder tippen" : "Erst Hersteller wählen"}
                  disabled={!vehicle.manufacturer}
                  inputClassName="h-14 text-base"
                  testId="self-input-model"
                  emptyLabel="Keine passenden Modelle gefunden"
                />
              </Field>
              <Field label="Baujahr">
                <Input value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} className="h-14" data-testid="self-input-year" />
              </Field>
              <Field label="Kennzeichen">
                <Input value={vehicle.license_plate} onChange={(e) => setVehicle({ ...vehicle, license_plate: e.target.value.toUpperCase() })} className="h-14" data-testid="self-input-plate" />
              </Field>
            </div>

            {/* VIN-Scheiben-Assistent */}
            <div className="mt-6 p-5 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-start gap-3 mb-3">
                <MagicWand size={22} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-bold text-slate-900">Scheiben-Assistent (VIN)</h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Fahrgestellnummer eingeben — wir zeigen dir automatisch alle passenden Scheiben für dein Fahrzeug.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-col sm:flex-row">
                <Input
                  value={vehicle.vin}
                  onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17) })}
                  placeholder="17-stellige VIN (Fahrgestellnummer)"
                  className="h-12 flex-1 font-mono tracking-wider text-sm"
                  maxLength={17}
                  data-testid="self-input-vin"
                />
                <Button
                  type="button"
                  onClick={decodeVin}
                  disabled={vinLoading || (vehicle.vin || "").length !== 17}
                  className="h-12 bg-blue-600 hover:bg-blue-700 sm:w-40"
                  data-testid="self-btn-vin-decode"
                >
                  {vinLoading ? <SpinnerGap size={16} className="mr-2 animate-spin" /> : <MagicWand size={16} className="mr-2" />}
                  VIN prüfen
                </Button>
              </div>

              {vinResult && vinResult.ok && (
                <div className="mt-4 space-y-3">
                  <div className="p-3 rounded-lg bg-white border border-blue-100 text-sm">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1 font-semibold">Erkannt</div>
                    <div className="font-semibold text-slate-900">
                      {vinResult.manufacturer} {vinResult.model || ""} {vinResult.year ? `· BJ ${vinResult.year}` : ""}
                    </div>
                    {vinResult.body_type && <div className="text-xs text-slate-500 mt-0.5">{vinResult.body_type}</div>}
                  </div>

                  {/* Scheiben-Position wählen */}
                  <div>
                    <div className="text-xs text-slate-600 uppercase tracking-wider mb-2 font-semibold">
                      Welche Scheibe soll ersetzt werden?
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {vinResult.glass_options.map((opt) => {
                        const active = glassPosition === opt.position_key;
                        return (
                          <button
                            key={opt.position_key}
                            type="button"
                            onClick={() => selectGlassPosition(opt.position_key)}
                            className={`px-3 py-2.5 rounded-lg border text-xs font-medium text-left transition-all ${
                              active
                                ? "border-blue-500 bg-blue-50 text-blue-900"
                                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                            }`}
                            data-testid={`glass-pos-${opt.position_key}`}
                          >
                            <div className="font-semibold">{opt.label}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {opt.price_low}–{opt.price_high} €
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Auto-erkannte Ausstattung (Badges) */}
                  {glassPosition && (() => {
                    const opt = vinResult.glass_options.find((o) => o.position_key === glassPosition);
                    const activeLabels = (opt?.features || [])
                      .filter((f) => glassFeatures[f.key])
                      .map((f) => f.label);
                    return (
                      <div>
                        <div className="text-xs text-slate-600 uppercase tracking-wider mb-2 font-semibold">
                          Automatisch erkannte Ausstattung
                        </div>
                        {activeLabels.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {activeLabels.map((label) => (
                              <span
                                key={label}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium"
                                data-testid="auto-feature-badge"
                              >
                                <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                {label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 italic">
                            Standard-Ausstattung (keine Sonderfeatures)
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Preis-Karte + Übernehmen-Button */}
                  {glassEstimate && (
                    <div className="space-y-2">
                      <div className="p-4 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-xs uppercase tracking-wider font-semibold opacity-80">
                            {vinResult.glass_options.find((o) => o.position_key === glassPosition)?.label}
                          </div>
                          <div className="text-xs opacity-70">inkl. 19% MwSt.</div>
                        </div>
                        <div className="text-3xl font-bold tracking-tight" data-testid="glass-price-preview">
                          {glassEstimate.low} – {glassEstimate.high} €
                        </div>
                        <div className="text-xs opacity-80 mt-1">
                          Anzahlung 25 %: {Math.round(((glassEstimate.low + glassEstimate.high) / 2) * 0.25)} €
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={useGlassPriceAsFinal}
                        disabled={loading}
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                        data-testid="glass-use-price-btn"
                      >
                        {loading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <CheckCircle size={18} weight="fill" className="mr-2" />}
                        Diesen Preis übernehmen & weiter
                      </Button>
                    </div>
                  )}

                  {/* Ausstattung anpassen (kollabiert) */}
                  {glassPosition && (
                    <details className="group">
                      <summary className="cursor-pointer text-xs text-slate-600 hover:text-blue-700 font-medium py-2 select-none">
                        Weicht die Ausstattung ab? Hier anpassen…
                      </summary>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {vinResult.glass_options
                          .find((o) => o.position_key === glassPosition)
                          ?.features.map((f) => {
                            const active = !!glassFeatures[f.key];
                            return (
                              <button
                                key={f.key}
                                type="button"
                                onClick={() => toggleGlassFeature(f.key)}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs transition-all ${
                                  active
                                    ? "border-blue-500 bg-blue-50 text-blue-900"
                                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                                }`}
                                data-testid={`glass-feat-${f.key}`}
                              >
                                <span className="text-left">{f.label}</span>
                                <span className={`text-[11px] font-mono ${active ? "text-blue-700" : "text-slate-400"}`}>
                                  {active ? "✓" : `+${f.price_add}€`}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>

            <Field label="Bekannte Ausstattung (optional)">
              <div className="grid grid-cols-2 gap-3">
                {FEATURES.map((f) => {
                  const active = features.includes(f.v);
                  return (
                    <button
                      key={f.v}
                      type="button"
                      onClick={() => setFeatures(active ? features.filter((x) => x !== f.v) : [...features, f.v])}
                      className={`h-14 px-4 rounded-xl border text-sm font-medium transition-all ${
                        active ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                      data-testid={`feature-${f.v}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Button
              onClick={computeEstimate}
              disabled={loading}
              className="h-14 w-full bg-blue-600 hover:bg-blue-700 text-base"
              data-testid="compute-estimate"
            >
              {loading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <CurrencyEur size={18} className="mr-2" />}
              Preis berechnen
            </Button>
          </Card>
        )}

        {/* STAGE 3: Estimate result */}
        {stage === 3 && estimate && (
          <Card icon={CurrencyEur} title="Geschätzter Preis inkl. Montage">
            <div className="text-center py-4">
              <p className="text-sm text-slate-500 uppercase tracking-widest font-semibold">Preisspanne</p>
              <p className="text-5xl font-bold text-slate-900 mt-2 tracking-tight" data-testid="estimate-range">
                {estimate.low.toFixed(0)} – {estimate.high.toFixed(0)} €
              </p>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">{estimate.explanation}</p>
            </div>
            {estimate.detected_features?.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center">
                {estimate.detected_features.map((f, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    {f}
                  </span>
                ))}
              </div>
            )}
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900 flex gap-2">
              <Info size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>
                Die Preisschätzung ist <strong>unverbindlich</strong>. Der genaue Preis hängt von Ausstattung
                und Verfügbarkeit der Scheibe ab. Wir bestätigen den Endpreis innerhalb von 24 Stunden.
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Anzahlung (25 %)</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{depositAmount.toFixed(2)} €</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Restzahlung vor Ort</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">Bar / EC</p>
              </div>
            </div>
            <Button onClick={() => setStage(4)} className="h-14 w-full bg-blue-600 hover:bg-blue-700 text-base" data-testid="continue-to-appointment">
              Termin sichern <ArrowRight size={18} className="ml-2" />
            </Button>
          </Card>
        )}

        {/* STAGE 4: Appointment + contact */}
        {stage === 4 && (
          <Card icon={CalendarBlank} title="Termin & Kontakt">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Datum</p>
                <Calendar
                  mode="single"
                  selected={parseLocalDateString(appointment.date)}
                  onSelect={(d) => d && setAppointment({ ...appointment, date: toLocalDateString(d) })}
                  disabled={(date) => isDateDisabled(date)}
                  data-testid="self-appointment-calendar"
                />
                <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900" data-testid="self-booking-hint">
                  {getBookingHint()}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Uhrzeit</p>
                <div className="grid grid-cols-2 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAppointment({ ...appointment, time: t })}
                      className={`h-14 rounded-xl border text-base font-semibold ${
                        appointment.time === t ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-900 border-slate-200"
                      }`}
                      data-testid={`self-time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 pt-3">
              <Field label="Name">
                <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="h-14" data-testid="self-input-name" />
              </Field>
              <Field label="E-Mail">
                <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="h-14" data-testid="self-input-email" />
              </Field>
              <Field label="Telefon">
                <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="h-14" data-testid="self-input-phone" />
              </Field>
            </div>
            <Button
              onClick={() => {
                if (!isValidEmail(contact.email)) {
                  toast.error("Bitte eine gültige E-Mail-Adresse angeben (z.B. max@beispiel.de)");
                  return;
                }
                setStage(5);
              }}
              disabled={!appointment.date || !appointment.time || !contact.name || !contact.email}
              className="h-14 w-full bg-blue-600 hover:bg-blue-700 text-base"
              data-testid="continue-to-deposit"
            >
              Weiter zur Anzahlung <ArrowRight size={18} className="ml-2" />
            </Button>
          </Card>
        )}

        {/* STAGE 5: Deposit */}
        {stage === 5 && estimate && (
          <Card icon={CreditCard} title="Anzahlung zur Terminsicherung">
            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Anzahlung 25 %</p>
              <p className="text-4xl font-bold text-slate-900 mt-1">{depositAmount.toFixed(2)} €</p>
              <p className="text-sm text-slate-500 mt-1">Restbetrag von ca. {(estimate.high - depositAmount).toFixed(0)} € zahlen Sie vor Ort.</p>
            </div>
            <ul className="grid grid-cols-2 gap-2 text-xs">
              {["Kreditkarte", "Apple Pay", "Google Pay", "PayPal"].map((p) => (
                <li key={p} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700">{p}</li>
              ))}
            </ul>
            <Button
              onClick={startDeposit}
              disabled={loading}
              className="h-14 w-full bg-blue-600 hover:bg-blue-700 text-base"
              data-testid="start-deposit"
            >
              {loading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <CreditCard size={18} className="mr-2" />}
              Jetzt {depositAmount.toFixed(2)} € anzahlen
            </Button>
            <p className="text-center text-xs text-slate-500">
              Bezahlung über Stripe — sicher und verschlüsselt.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5 animate-fade-up shadow-sm">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
        <Icon size={24} weight="duotone" color="white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);
